import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMessaging } from "@/context/MessagingContext";
import { fileUrl } from "@/lib/fileUrl";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Paperclip, X, FileText, Check, CheckCheck, Smile, ArrowLeft } from "lucide-react";

const EMOJIS = ["😀", "😂", "😊", "👍", "🙏", "🎉", "🔥", "❤️", "👏", "✅", "💯", "🤝", "📞", "📅", "💡", "👋", "🚀", "😎", "🤔", "👀", "💰", "📈", "⭐", "😅"];

const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const Attachment = ({ a }) => {
  const isImg = (a.type || "").startsWith("image/");
  if (isImg) {
    return <a href={fileUrl(a.url)} target="_blank" rel="noreferrer" data-testid="chat-attachment-image">
      <img src={fileUrl(a.url)} alt={a.name} className="mt-1 max-h-48 rounded-lg border border-border object-cover" />
    </a>;
  }
  return <a href={fileUrl(a.url)} target="_blank" rel="noreferrer" data-testid="chat-attachment-file"
    className="mt-1 flex items-center gap-2 rounded-lg border border-border bg-background/60 px-3 py-2 text-xs hover:bg-accent">
    <FileText className="h-4 w-4 text-primary" /> <span className="truncate">{a.name}</span>
  </a>;
};

export const ChatThread = ({ compact = false, onBack }) => {
  const { user } = useAuth();
  const { active, messages, othersLastRead, typing, activeId, sendMessage, sendTyping } = useMessaging();
  const [text, setText] = useState("");
  const [pending, setPending] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const lastTyped = useRef(0);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, typing]);

  const onType = (e) => {
    setText(e.target.value);
    const now = Date.now();
    if (now - lastTyped.current > 1500) { lastTyped.current = now; sendTyping(); }
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      try {
        const { data } = await api.post("/conversations/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPending((p) => [...p, data]);
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    const t = text.trim();
    if (!t && !pending.length) return;
    setBusy(true);
    try { await sendMessage(t, pending); setText(""); setPending([]); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const typer = activeId ? typing[activeId] : null;
  const myLastMsg = [...messages].reverse().find((m) => m.sender_id === user.user_id);

  return (
    <div className="flex h-full flex-col" data-testid="chat-thread">
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        {onBack && (
          <button onClick={onBack} data-testid="chat-back-button" className="-ml-1 rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground md:hidden" aria-label="Back to conversations">
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        <div className="min-w-0">
          <p className="truncate font-heading text-sm font-semibold">{active?.title || "Conversation"}</p>
          <p className="truncate text-xs text-muted-foreground">{active?.subtitle}</p>
        </div>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="chat-messages">
        {messages.length === 0 && <p className="pt-8 text-center text-sm text-muted-foreground">No messages yet. Say hello 👋</p>}
        {messages.map((m) => {
          const mine = m.sender_id === user.user_id;
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${m.message_id}`}>
              <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 ${mine ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
                {!mine && <p className="mb-0.5 text-[11px] font-semibold opacity-80">{m.sender_name}{m.org_name ? ` · ${m.org_name}` : ""}</p>}
                {m.text && <p className="whitespace-pre-wrap break-words text-sm">{m.text}</p>}
                {(m.attachments || []).map((a, i) => <Attachment key={i} a={a} />)}
                <p className={`mt-1 text-right text-[10px] ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{fmtTime(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        {myLastMsg && (
          <div className="flex justify-end pr-1" data-testid="read-receipt">
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
              {othersLastRead && othersLastRead >= myLastMsg.created_at
                ? <><CheckCheck className="h-3 w-3 text-primary" /> Seen</>
                : <><Check className="h-3 w-3" /> Sent</>}
            </span>
          </div>
        )}
        {typer && <p className="text-xs italic text-muted-foreground" data-testid="typing-indicator">{typer.name} is typing…</p>}
        <div ref={endRef} />
      </div>

      {pending.length > 0 && (
        <div className="flex flex-wrap gap-2 border-t border-border px-4 py-2" data-testid="pending-attachments">
          {pending.map((a, i) => (
            <span key={i} className="flex items-center gap-1.5 rounded-full border border-border bg-muted px-2 py-1 text-xs">
              <FileText className="h-3 w-3" /> <span className="max-w-[120px] truncate">{a.name}</span>
              <button onClick={() => setPending((p) => p.filter((_, j) => j !== i))} data-testid={`remove-attachment-${i}`}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}

      <div className="relative border-t border-border p-3">
        {showEmoji && (
          <div className="absolute bottom-16 left-3 z-10 grid grid-cols-8 gap-1 rounded-lg border border-border bg-card p-2 shadow-lg" data-testid="emoji-picker">
            {EMOJIS.map((e) => (
              <button key={e} type="button" data-testid={`emoji-${e}`} onClick={() => { setText((t) => t + e); setShowEmoji(false); }}
                className="rounded p-1 text-lg transition-colors hover:bg-accent">{e}</button>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <input ref={fileRef} type="file" multiple hidden onChange={onFiles}
            accept="image/*,.pdf,.csv,.txt,.doc,.docx,.xls,.xlsx" data-testid="chat-file-input" />
          <Button size="icon" variant="ghost" onClick={() => setShowEmoji((s) => !s)} title="Emoji" data-testid="chat-emoji-button">
            <Smile className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" onClick={() => fileRef.current?.click()} disabled={uploading} title="Attach" data-testid="chat-attach-button">
            <Paperclip className="h-4 w-4" />
          </Button>
          <Textarea rows={1} value={text} onChange={onType} placeholder="Write a message…"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
            className="max-h-32 min-h-[40px] flex-1 resize-none" data-testid="chat-input" />
          <Button size="icon" onClick={submit} disabled={busy || uploading} data-testid="chat-send-button"><Send className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
};
